"use client";

import * as React from "react";
import {
	CheckIcon,
	Copy,
	Download,
	PaperclipIcon,
	ThumbsDownIcon,
	ThumbsUpIcon,
} from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "../../lib/utils";
import {
	extractChainOfThoughtTexts,
	extractMessageContent,
	extractTextWithState,
} from "./extract-message-content";
import { toReasoningSteps } from "./reasoning-steps";
import { formatDuration } from "./format-duration";
import { isRelativeLinkOnly, type IsInternalLink } from "./link-safety";
import { ChatStatusRow } from "./loader";
import {
	Message,
	MessageAction,
	MessageActions,
	MessageContent,
	MessageResponse,
} from "./message";
import {
	presentMessage,
	resolveMessagePhase,
	type MessagePhase,
} from "./message-phase";
import { Reasoning, ReasoningContent, ReasoningTrigger } from "./reasoning";
import { StatusBadge, type StatusBadgeProps } from "./status-badge";

type BadgeVariant = StatusBadgeProps["variant"];
import {
	readStatusTag,
	STATUS_TAG,
	STATUS_TAG_ATTRIBUTES,
	stripCustomTag,
	type StatusDescriptor,
	type StatusToken,
} from "./status-token";
import type { ChatMessageAttachment, ChatUIMessage } from "./types";

function AttachmentThumbnail({
	attachment,
	onError,
}: {
	attachment: ChatMessageAttachment;
	onError: () => void;
}) {
	// Plain <img>: the src is a presigned S3 URL that next/image is not configured to load.
	const image = (
		<img
			alt={attachment.filename}
			className="max-h-40 max-w-60 rounded-md border border-neutral-200 object-contain dark:border-neutral-800"
			loading="lazy"
			onError={onError}
			src={attachment.previewUrl ?? undefined}
		/>
	);

	return attachment.downloadUrl ? (
		<a
			aria-label={`Download ${attachment.filename}`}
			href={attachment.downloadUrl}
			rel="noopener noreferrer"
			target="_blank"
		>
			{image}
		</a>
	) : (
		image
	);
}

function ChatMessageAttachments({
	attachments,
	align = "end",
	showPreviews,
}: {
	attachments: ChatMessageAttachment[];
	align?: "start" | "end";
	showPreviews: boolean;
}) {
	// Keyed by URL, so a refetch that mints a fresh preview URL retries the image.
	const [failedPreviews, setFailedPreviews] = useState<ReadonlySet<string>>(
		() => new Set(),
	);

	if (attachments.length === 0) {
		return null;
	}

	return (
		<div
			className={cn(
				"mt-1 flex flex-wrap gap-1.5",
				align === "start" ? "justify-start" : "justify-end",
			)}
		>
			{attachments.map((attachment) => {
				const { previewUrl } = attachment;
				if (showPreviews && previewUrl && !failedPreviews.has(previewUrl)) {
					return (
						<AttachmentThumbnail
							attachment={attachment}
							key={attachment.documentId}
							onError={() =>
								setFailedPreviews((prev) => new Set(prev).add(previewUrl))
							}
						/>
					);
				}

				const inner = (
					<>
						<PaperclipIcon className="size-3 shrink-0" />
						<span className="max-w-[360px] truncate">
							{attachment.filename}
						</span>
						{attachment.downloadUrl && <Download className="size-3 shrink-0" />}
					</>
				);

				const shared =
					"inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-2 py-1 text-xs";

				return attachment.downloadUrl ? (
					<a
						className={cn(
							shared,
							"bg-neutral-50 text-neutral-700 transition-colors hover:bg-neutral-100",
						)}
						href={attachment.downloadUrl}
						key={attachment.documentId}
						rel="noopener noreferrer"
						target="_blank"
					>
						{inner}
					</a>
				) : (
					<span
						className={cn(shared, "bg-neutral-50 text-neutral-500")}
						key={attachment.documentId}
					>
						{inner}
					</span>
				);
			})}
		</div>
	);
}

function CopyButton({ content }: { content: string }) {
	const [copied, setCopied] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		},
		[],
	);

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(content);
			setCopied(true);
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			timeoutRef.current = setTimeout(() => setCopied(false), 2000);
		} catch {
			// The Clipboard API is unavailable in insecure contexts. Nothing to say.
		}
	}, [content]);

	return (
		<MessageAction
			className="text-neutral-500"
			onClick={handleCopy}
			tooltip={copied ? "Copied" : "Copy"}
		>
			{copied ? (
				<CheckIcon className="size-3.5" />
			) : (
				<Copy className="size-3.5" />
			)}
		</MessageAction>
	);
}

export type ChatMessageProps = {
	message: ChatUIMessage;
	/**
	 * Where this message is in its life.
	 *
	 * Omitted, it is derived from the message's own parts and `isActive`. Passing
	 * it explicitly is what makes every state reachable from a single render,
	 * which is what a story or a test needs.
	 */
	phase?: MessagePhase;
	/** Whether the current run is writing into THIS message. Ignored when `phase` is given. */
	isActive?: boolean;
	/** Seconds between the question and the answer, when the host knows it. */
	duration?: number;
	onRate?: (messageId: string, rating: number | null) => void;
	allowLink?: IsInternalLink;
	/** Status line while the turn has produced nothing yet. */
	pendingLabel?: ReactNode;
	/** Hide that status line, for a host showing its own row in its place. */
	hidePending?: boolean;
	/**
	 * Tool output rendered above the answer.
	 *
	 * A render prop because which tools get a card, and what those cards look
	 * like, is the host's business — the package has no registry of its own.
	 */
	renderToolParts?: (message: ChatUIMessage) => ReactNode;
	/**
	 * The user's own turn.
	 *
	 * Overridable because a host may encode more than prose in it — entity
	 * mentions, for instance, which are a wire convention rather than a UI one.
	 */
	renderUserContent?: (message: ChatUIMessage, text: string) => ReactNode;
	/**
	 * Rendered last, outside the bubble.
	 *
	 * For anything a host hangs off a message that is not part of its content —
	 * entity link buttons, say, which sit under the bubble rather than in it.
	 */
	renderFooter?: (message: ChatUIMessage, text: string) => ReactNode;
	/**
	 * Turn a status token into a label and a colour.
	 *
	 * Omitted, or returning null, leaves the token rendering as ordinary inline
	 * code — which is exactly how an answer behaves today, before atlas emits any.
	 * That is the whole backward-compatibility story: no token, no change.
	 */
	resolveStatus?: (token: StatusToken) => StatusDescriptor | null;
	formatTimestamp?: (date: Date) => string;
	/** Render image attachments that carry a `previewUrl` as thumbnails; false keeps the filename pill. */
	showAttachmentPreviews?: boolean;
	className?: string;
};

const defaultFormatTimestamp = (date: Date) =>
	date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/**
 * One message bubble, user or assistant.
 *
 * Every visual decision comes from `presentMessage(phase)` — a pure table — so
 * the component holds no phase state of its own. Its only state is the copy
 * button's confirmation and the reader's manual collapse of the reasoning panel.
 */
export function ChatMessage({
	message,
	phase: phaseProp,
	isActive = false,
	duration,
	onRate,
	allowLink = isRelativeLinkOnly,
	pendingLabel = "Thinking...",
	hidePending = false,
	renderToolParts,
	renderUserContent,
	renderFooter,
	resolveStatus,
	formatTimestamp = defaultFormatTimestamp,
	showAttachmentPreviews = true,
	className,
}: ChatMessageProps) {
	const isUser = message.role === "user";
	const phase = phaseProp ?? resolveMessagePhase(message, { isActive });

	const { text: content, isStreaming: isTextStreaming } =
		extractTextWithState(message);
	// The steps, not the joined blob: `ReasoningContent` renders one accordion row
	// per step, and joining them first threw away the boundaries it needs.
	const reasoningSteps = isUser ? [] : extractChainOfThoughtTexts(message);
	// The count the header shows has to be the count the reader can see, so it comes
	// from the same normalisation the content renders from — blanks and adjacent
	// duplicates are dropped, and a raw `length` would over-report.
	const resolvedReasoningSteps = toReasoningSteps(reasoningSteps);
	const reasoningStepCount = resolvedReasoningSteps.length;
	// The step running right now, which the summary line names while streaming. The
	// last one resolved is the current one - they arrive in order.
	const currentReasoningStep = resolvedReasoningSteps.at(-1)?.label;
	const hasReasoning = reasoningStepCount > 0;

	const view = presentMessage(phase, { hasReasoning });
	const showLoader = view.showLoader && !hidePending;
	const isLive =
		phase === "pending" || phase === "reasoning" || phase === "streaming";

	const toolParts = isUser ? null : renderToolParts?.(message);

	/**
	 * Upgrade a `<status>` tag into a badge.
	 *
	 * Built only when a host supplies a resolver, so the default render path is
	 * byte-identical to before. A tag the resolver does not recognise falls through
	 * to its own child text — never a guessed label.
	 */
	type MarkdownComponents = ComponentProps<
		typeof MessageResponse
	>["components"];
	const markdownComponents = React.useMemo<MarkdownComponents>(() => {
		if (!resolveStatus) {
			return undefined;
		}

		return {
			// Keyed on the custom tag, never on `code`: an earlier version overrode
			// `code` and silently broke every fenced block in an answer.
			[STATUS_TAG]: ({ children, ...props }) => {
				const token = readStatusTag(props as Record<string, unknown>);
				const descriptor = token ? resolveStatus(token) : null;

				// No attributes, or an enum/value this build does not know: fall back to
				// the tag's own child text, which is what an un-upgraded client shows.
				if (!descriptor) {
					return <>{children}</>;
				}

				return (
					<StatusBadge
						label={descriptor.label}
						variant={descriptor.variant as BadgeVariant}
					/>
				);
			},
		};
	}, [resolveStatus]);
	const attachments = message.attachments ?? [];
	const footer = renderFooter?.(message, content);

	// Nothing to draw. An empty user turn with no files is not a message, and an
	// assistant turn with no content, no reasoning, no tools and no footer has not
	// started. A footer alone still counts: a run stopped before its first token
	// is marked by the host there.
	if (isUser && !content && attachments.length === 0) {
		return null;
	}
	if (
		!isUser &&
		!content &&
		!hasReasoning &&
		!showLoader &&
		!toolParts &&
		!footer
	) {
		return null;
	}

	return (
		<Message
			className={className}
			data-testid={`ai-chat-message-${message.role}`}
			from={message.role}
		>
			{isUser ? (
				content ? (
					<MessageContent>
						{renderUserContent?.(message, content) ?? (
							<p className="whitespace-pre-wrap text-[.9125rem]">{content}</p>
						)}
					</MessageContent>
				) : null
			) : (
				<MessageContent>
					{hasReasoning && (
						// Remounted when the message crosses into or out of a live phase, so a
						// finished answer that starts regenerating opens its panel again. The
						// panel ignores `defaultOpen` changes after mount by design.
						<Reasoning
							data-testid="ai-chat-reasoning"
							defaultOpen={view.reasoningOpen}
							// Any settled turn, not just one loaded from history: the host derives
							// this from the two timestamps, which are only meaningful once the answer
							// has landed. Withheld while live so the panel times its own run instead.
							duration={isLive ? undefined : duration}
							currentStep={currentReasoningStep}
							isStreaming={view.reasoningStreaming}
							key={isLive ? "live" : "idle"}
							stepCount={reasoningStepCount}
						>
							<ReasoningTrigger />
							<ReasoningContent allowLink={allowLink}>
								{reasoningSteps}
							</ReasoningContent>
						</Reasoning>
					)}

					{showLoader && (
						<ChatStatusRow
							data-testid="ai-chat-thinking"
							label={pendingLabel}
						/>
					)}

					{toolParts}

					{view.showContent && content && (
						<MessageResponse
							allowLink={allowLink}
							allowedTags={
								resolveStatus
									? { [STATUS_TAG]: [...STATUS_TAG_ATTRIBUTES] }
									: undefined
							}
							components={markdownComponents}
							// The child text is a plain label, not markdown to re-parse.
							literalTagContent={resolveStatus ? [STATUS_TAG] : undefined}
							isAnimating={view.animateContent && isTextStreaming}
						>
							{content}
						</MessageResponse>
					)}

					{/* Only when there is no reasoning panel, which already reports this. */}
					{!isLive &&
						!hasReasoning &&
						duration !== undefined &&
						duration >= 0 && (
							<p
								className="mt-3 text-xs text-neutral-500"
								data-testid="ai-chat-message-duration"
							>
								Thought for {formatDuration(duration)}
							</p>
						)}
				</MessageContent>
			)}

			{/* Always visible rather than hover-revealed: these are the only way to copy
			    or rate an answer, and hover-only controls are undiscoverable on touch. */}
			{!isUser && view.showActions && content && (
				<MessageActions className="mt-1">
					{/* Stripped HERE, not inside `extractMessageContent` — that feeds
					    `extractTextWithState`, which is what RENDERS the answer, so
					    stripping there takes the tag away from the badge too. The copy is
					    the only place that wants the label without its markup. */}
					<CopyButton
						content={stripCustomTag(extractMessageContent(message), STATUS_TAG)}
					/>
					{/* The selected state is the violet chip and stroke, not a filled glyph —
					    lucide's default `fill="none"` is left alone deliberately. */}
					<MessageAction
						className={cn(
							"text-neutral-500",
							message.rating === 1 &&
								"bg-primary-100 text-primary hover:bg-primary-100",
						)}
						onClick={() =>
							onRate?.(message.id, message.rating === 1 ? null : 1)
						}
						tooltip="Good response"
					>
						<ThumbsUpIcon className="size-3.5" />
					</MessageAction>
					<MessageAction
						className={cn(
							"text-neutral-500",
							message.rating === 0 &&
								"bg-primary-100 text-primary hover:bg-primary-100",
						)}
						onClick={() =>
							onRate?.(message.id, message.rating === 0 ? null : 0)
						}
						tooltip="Bad response"
					>
						<ThumbsDownIcon className="size-3.5" />
					</MessageAction>
				</MessageActions>
			)}

			{isUser && message.createdAt && (
				<span className="ml-auto mt-1 text-xs text-neutral-400">
					{formatTimestamp(message.createdAt)}
				</span>
			)}

			{attachments.length > 0 && (
				<ChatMessageAttachments
					align={isUser ? "end" : "start"}
					attachments={attachments}
					showPreviews={showAttachmentPreviews}
				/>
			)}

			{footer}
		</Message>
	);
}
